/**
 * Utility functions for text processing
 */

/**
 * Return a word/character count that works for texts containing a mix of
 * English (or other Latin-alphabet) words and Chinese characters.
 * 
 * Counting rules:
 * • A contiguous sequence of ASCII letters and/or digits ([A-Za-z0-9]+)
 *   is treated as one word.
 * • Every individual CJK Unified Ideograph ([\u4E00-\u9FFF]) counts as
 *   one word.
 * 
 * This heuristic matches typical expectations for credit calculation where
 * English words and Chinese characters should incur the same cost unit.
 */
export function mixedLanguageWordCount(text: string): number {
  if (!text) {
    return 0;
  }

  // Match either an alphanumeric token or a single CJK character
  const tokens = text.match(/[A-Za-z0-9]+|[\u4E00-\u9FFF]/g);
  return tokens ? tokens.length : 0;
}

/**
 * Calculate credit cost based on word count
 * 1 credit per 1000 words/characters, minimum 1
 */
export function calculateCreditCost(wordCount: number): number {
  return Math.max(1, Math.floor(wordCount / 1000));
}

/**
 * Get credit cost information for a given text
 */
export function getCreditCostInfo(content: string) {
  const wordCount = mixedLanguageWordCount(content);
  const creditCost = calculateCreditCost(wordCount);
  const isPartialContent = wordCount < 1000;
  return { wordCount, creditCost, isPartialContent };
} 